import { Injectable, inject } from '@angular/core';
import { WINDOW } from '@shared/dom';

@Injectable({
  providedIn: 'root',
})
export class MediaDevicesService {
  private readonly window = inject<Window>(WINDOW);

  private readonly activeMediaStreams = [];

  /**
   * Request an audio stream from the device.
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  public getAudioStream() {
    return this.getUserMedia({
      audio: {
        channelCount: 1, // mono
        echoCancellation: true, // NB: If false, recording on Samsung Note 10+ goes very bad.
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
  }

  /**
   * Request a video stream from the device.
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  public async getVideoStream(): Promise<MediaStream> {
    try {
      return await this.getPreferredVideoStream();
    } catch {
      return await this.getAnyVideoStream();
    }
  }

  public async getVideoDeviceList(): Promise<MediaDeviceInfo[]> {
    const allDevices = await this.window.navigator.mediaDevices.enumerateDevices();
    return allDevices.filter((device) => device.kind === 'videoinput' && !!device.deviceId && device.deviceId.trim() !== '');
  }

  /**
   * Request a specific device by its id.
   *
   * @param deviceId which can be obtained from the BrowserCompatibilityService.
   * @see BrowserCompatibilityService#videoInputs.
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  public getSpecificVideoStream(deviceId: string): Promise<MediaStream> {
    const mediaStreamConstraints: MediaStreamConstraints = {
      audio: false,
      video: { deviceId: { exact: deviceId } },
    };
    return this.getUserMedia(mediaStreamConstraints);
  }

  /**
   * Request a video stream with our preferred configuration.
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  private getPreferredVideoStream(): Promise<MediaStream> {
    const mediaStreamConstraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: 'user',
        width: 640,
      },
    };
    return this.getUserMedia(mediaStreamConstraints);
  }

  /**
   * Request a video stream with our minimum requirements.
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  private getAnyVideoStream(): Promise<MediaStream> {
    const mediaStreamConstraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: 'user',
      },
    };
    return this.getUserMedia(mediaStreamConstraints);
  }

  /**
   * Request a user media stream from the device.
   * @param constraints The media request constraints
   * @returns A promise that may resolve with the stream if the user accepts.
   */
  private async getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
    try {
      const stream = await this.window.navigator.mediaDevices.getUserMedia(constraints);
      this.activeMediaStreams.push(stream);
      return stream;
    } catch (error) {
      console.error(`navigator.mediaDevices.getUserMedia threw the following error: ${error}`);
      throw error;
    }
  }

  /**
   * Stops the given stream, freeing up resources connected to it.
   * @param stream The stream to stop.
   */
  public stopStream(stream: MediaStream): void {
    this.stopAllTracks(stream);
    this.removeStream(stream);
  }

  /**
   * Stop all tracks on a stream.
   * @param stream The stream to stop all tracks for.
   */
  private stopAllTracks(stream: MediaStream): void {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }

  /**
   * Removes a stream from our stream list if it's present.
   * @param stream The stream to remove.
   */
  private removeStream(stream: MediaStream): void {
    for (let i = 0; i < this.activeMediaStreams.length; i++) {
      if (this.activeMediaStreams[i] === stream) {
        this.activeMediaStreams.splice(i, 1);
      }
    }
  }

  /**
   * Stop all streams in the stream list, freeing up all resources acquired so far.
   */
  public stopAllStreams(): void {
    if (this.activeMediaStreams.length > 0) {
      console.log(`Found ${this.activeMediaStreams.length} open streams; closing them.`);
    }
    while (this.activeMediaStreams.length > 0) {
      const stream = this.activeMediaStreams.pop();
      this.stopAllTracks(stream);
    }
  }
}
